import React, { useRef, useEffect, useState, useLayoutEffect } from 'react'
import gsap from 'gsap'
import { useRenderCounter } from '../../../lib/hooks/useRenderCounter'
import { LABEL_SHADOW_ID } from '../constants'

const INNER_HORIZONTAL_PADDING = 2

interface Props {
  className?: string
  x: number
  y: number
  yText: number
  height: number
  animationDurationSec?: number
  animationEnter?: Object
  [key: string]: any // any other prop
  children: React.ReactNode | React.ReactNode[]
}

export const AnimatedText: React.FC<Props> = ({
  className = '',
  x,
  y,
  yText,
  height,
  animationDurationSec = 0.3,
  opacity,
  children,
  animationEnter = {},
  ...props
}) => {
  const textRef = useRef(null)
  const rectRef = useRef(null)
  const renderCount = useRenderCounter()
  const [textWidth, setTextWidth] = useState(0)

  useLayoutEffect(() => {
    if (textRef.current) {
      const textWidth = textRef.current.getBBox().width
      setTextWidth(textWidth + 2 * INNER_HORIZONTAL_PADDING)

      const timing = { duration: animationDurationSec }
      const toAnimateText = {
        translateX: x + INNER_HORIZONTAL_PADDING,
        translateY: yText,
        opacity,
      }
      const toAnimateRect = { translateX: x, translateY: y, opacity }
      if (renderCount === 0) {
        gsap.set(textRef.current, { ...toAnimateText, ...animationEnter })
        gsap.set(rectRef.current, { ...toAnimateRect, ...animationEnter })
        gsap.to(textRef.current, { ...toAnimateText, ...timing })
        gsap.to(rectRef.current, { ...toAnimateRect, ...timing })
      } else {
        // NOTE: We tried animating x and y but GSAP doesn't animate with x and y attributes but with a transform matrix
        gsap.to(textRef.current, { ...toAnimateText, ...timing })
        gsap.to(rectRef.current, { ...toAnimateRect, ...timing })
      }
    }
  }, [x, y, opacity])

  return (
    <g className={`-animated-text ${className}`}>
      <rect
        ref={rectRef}
        {...props}
        fill="white"
        width={textWidth}
        height={height}
        rx={4}
        filter={`url(#${LABEL_SHADOW_ID})`}
      />

      <text ref={textRef} {...props}>
        {React.Children.map(children, (c, i) => {
          return (
            <tspan key={i} x={0} dy={i * 13}>
              {c}
            </tspan>
          )
        })}
      </text>
    </g>
  )
}
